import {
  Box,
  Center,
  Divider,
  Icon,
  Skeleton,
  Text,
  makeUclComponent,
} from '@rbilabs/universal-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import QRCode from 'components/qrcode';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { primitive } from 'styles/constants/primitives';

export const ShortCodeContent = Box.withConfig<{ newUI?: boolean }>(p => ({
  paddingX: '$6',
  marginX: 'auto',
  marginBottom: '$2',
  borderRadius: Styles.borderRadius,
  backgroundColor: primitive.bk.$crownColor,
  width: 'auto',
  minWidth: '$64',
}));

export const ShortCodeText = Text.withConfig<{ newUI?: boolean }>(p => ({
  fontFamily: 'Flame-Bold',
  color: primitive.bk.$bbqBrown,
  fontSize: '6xl',
  textAlign: 'center',
  lineHeight: 'xs',
}));

export const ShortCodeErrorContainer = Center.withConfig<{ newUI?: boolean }>(p => ({
  borderStyle: 'solid',
  borderWidth: '1',
  borderColor: Styles.color.grey.five,
  paddingY: '$4',
  paddingX: '$7',
  marginX: 'auto',
  marginTop: '$6',
  marginBottom: '$8',
  borderRadius: Styles.borderRadius,
  backgroundColor: p.newUI ? null : Styles.color.black,
  width: 'auto',
  minWidth: '$64',
  minHeight: '$24',
}));

export const StyledShortCodeErrorActionButton = makeUclComponent(ActionButton).withConfig<{
  newUI?: boolean;
}>(p => ({
  variant: p.newUI ? ActionButtonVariants.PRIMARY : ActionButtonVariants.INVERSE,
  marginTop: '$2',
  marginX: 'auto',
  marginBottom: '$0',
  fontSize: 'sm',
  paddingY: '$2.5',
  paddingX: '$8',
}));

export const ShortCodeContainer = Box.withConfig({
  flex: '1',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '$4',
});

export const ShortCodeTitle = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.bk.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'lg',
    fontWeight: 'bold',
  },
  marginLeft: '$1',
});

export const ShortCodeSubtitle = SanityBlockRenderer.withConfig({
  textProps: { color: primitive.bk.$bbqBrown, fontSize: 'sm', textAlign: 'center' },
});

export const ShortCodeTitleRedesign = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.bk.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'md',
  },
  marginLeft: '$1',
  marginBottom: '$4',
});

export const ShortCodeSubtitleRedesign = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.bk.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'md',
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

export const StyledRefreshIcon = Icon.withConfig({
  variant: 'refresh',
  color: Styles.color.black,
});

export const ErrorMessage = Text.withConfig({
  margin: '$0',
  color: primitive.bk.$mayoEggWhite,
});

export const ErrorMessageTitle = ErrorMessage.withConfig({
  fontWeight: 'bold',
  color: primitive.bk.$mayoEggWhite,
});

export const QRCodeContainer = Box.withConfig<{ newUI?: boolean }>(p => ({
  width: 'full',
  alignItems: 'center',
  alignSelf: 'center',
  flexDirection: 'column',
  alignContent: 'center',
  backgroundColor: primitive.bk.$crownColor,
  minHeight: '$64',
}));

export const ShortCodeWidgetContainer = Box.withConfig<{
  isQrEnabled?: boolean;
}>(p => ({
  background: primitive.bk.$crownColor,
  flexDirection: 'column',
  alignSelf: 'center',
  width: 'full',
  borderRadius: p.isQrEnabled ? 0 : 10,
  borderTopLeftRadius: 10,
  borderTopRightRadius: 10,
}));

export const ShortCodeWidgetContainerRedesign = Box.withConfig({
  background: primitive.bk.$crownColor,
  flex: 1,
  flexDirection: 'column',
  alignSelf: 'center',
  width: 'full',
  borderBottomRadius: 0,
  borderTopLeftRadius: 10,
  borderTopRightRadius: 10,
});

export const ShortCodeRefreshBtnContainer = Box.withConfig({
  flex: 1,
  alignSelf: 'center',
  width: '$56',
  paddingY: '$7',
  paddingX: '$10',
});

export const ShortCodeRefreshButton = makeUclComponent(ActionButton).withConfig<{
  isLoading?: boolean;
}>(p => ({
  width: 'full',
  variant: p.isLoading ? ActionButtonVariants.PRIMARY : ActionButtonVariants.OUTLINE,
  backgroundColor: primitive.$white,
}));

export const QRCodeTitleContainer = Box.withConfig({
  flex: '1',
  flexDirection: 'row',
});

export const QRCodeTitle = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.bk.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'lg',
    fontWeight: 'bold',
  },
  marginLeft: '$1',
});

export const QRCodeTitleRedesign = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.bk.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'md',
    fontWeight: 'bold',
  },
  marginLeft: '$1',
});

export const QRCodeSubtitle = SanityBlockRenderer.withConfig<{ newUI?: boolean }>(p => ({
  textProps: { color: primitive.bk.$bbqBrown, fontSize: 'sm' },
  marginBottom: '$1',
}));

export const StyledQRCode = QRCode.withConfig({
  marginTop: '$2',
  marginBottom: '$8',
  width: '$40',
  height: '$40',
  borderRadius: 'sm',
});

export const StyledQRSkeleton = Skeleton.withConfig({
  size: '40',
  marginBottom: '$6',
  borderRadius: 'lg',
});

export const Footer = Box.withConfig({
  backgroundColor: primitive.$whiteOpacity30,
  padding: '$3',
  width: 'full',
  alignItems: 'center',
});

export const QrDivider = Divider.withConfig({
  width: '4/5',
  marginBottom: '$4',
  background: primitive.$blackOpacity20,
});

export const CodeHeaderText = SanityBlockRenderer.withConfig({
  textProps: {
    textAlign: 'center',
    fontFamily: 'heading',
    fontSize: '2xl',
    lineHeight: '2xl',
    marginBottom: 0,
  },
});

export const TermsContainer = Box.withConfig({
  justifyContent: 'center',
  marginTop: '$2',
  marginBottom: '$3',
});

export const TermsText = SanityBlockRenderer.withConfig({
  textProps: {
    textAlign: 'center',
    fontSize: '2xs',
    lineHeight: 'lg',
    marginLeft: '$10',
    marginRight: '$10',
  },
});
